import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';

/**
 * Optional structured context for a log entry. Values are NOT redacted —
 * pass only safe identifiers (correlationId, action name, etc.). For raw
 * error objects, pass them as the `error` parameter so they get sanitized.
 */
export interface SafeLogContext {
  [key: string]: unknown;
}

const SENSITIVE_KEYS = new Set<string>([
  'accountnumber',
  'account_number',
  'accountid',
  'account_id',
  'accountidentifier',
  'iban',
  'phone',
  'phonenumber',
  'phone_number',
  'mobile',
  'mobilenumber',
  'mobile_number',
  'email',
  'password',
  'token',
  'access_token',
  'refresh_token',
  'authorization',
  'cookie',
  'set-cookie',
  'pin',
  'otp',
]);

const MAX_DEPTH = 4;

/**
 * PII-aware logger. Logs to `console` in dev, sanitizes error objects so
 * account numbers, IBANs, phones, emails, auth tokens, cookies, etc. don't
 * leak into devtools or any monitoring sink that scrapes console output.
 *
 * Use this instead of raw `console.*` in any code path that might log an
 * `HttpErrorResponse`, a request/response body, or any user data.
 *
 * For known-safe messages, `info(msg)` and `warn(msg)` accept a plain
 * string and pass through unchanged.
 */
@Injectable({ providedIn: 'root' })
export class SafeLogger {
  error(message: string, error?: unknown, context?: SafeLogContext): void {
    // eslint-disable-next-line no-console
    console.error(message, this.sanitize(error), context ?? '');
  }

  warn(message: string, error?: unknown, context?: SafeLogContext): void {
    // eslint-disable-next-line no-console
    console.warn(message, this.sanitize(error), context ?? '');
  }

  info(message: string, context?: SafeLogContext): void {
    // eslint-disable-next-line no-console
    console.info(message, context ?? '');
  }

  /**
   * Recursively walks a value and replaces sensitive fields with '[REDACTED]'.
   * Special-cases `HttpErrorResponse` and `Error` to keep only the
   * shape that's useful for diagnostics (status, name, message) and to
   * deliberately drop headers and request/response bodies.
   */
  private sanitize(value: unknown, depth = 0): unknown {
    if (value == null || depth > MAX_DEPTH) {
      return value ?? null;
    }

    if (value instanceof HttpErrorResponse) {
      return {
        kind: 'HttpErrorResponse',
        status: value.status,
        statusText: value.statusText,
        message: value.message,
        // headers + body intentionally dropped
      };
    }

    if (value instanceof Error) {
      return {
        kind: value.name,
        message: value.message,
        // stack intentionally kept for diagnostics
        stack: value.stack,
      };
    }

    if (Array.isArray(value)) {
      return value.map((item) => this.sanitize(item, depth + 1));
    }

    if (typeof value === 'object') {
      const out: Record<string, unknown> = {};
      for (const [key, val] of Object.entries(value)) {
        out[key] = SENSITIVE_KEYS.has(key.toLowerCase())
          ? '[REDACTED]'
          : this.sanitize(val, depth + 1);
      }
      return out;
    }

    return value;
  }
}
